import { Button } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { getItemWebStorage, setItemWebStorage } from '../../features/webStorageSaver/webStorageSaver';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import ContentHeader from '../../widgets/contentHeader/ContentHeader';
import { useAppSelector } from '../../stateManagement/hooks/redux';
import { auxiliaryLayersInitialDisplayState, resetAllMapLayersState, rosreestrLayersInitialDisplayState, setMapLayersState } from '../../stateManagement/reducers';
import { Tree } from 'primereact/tree';
import { auxiliaryLayersStructure, configureLayersTreeNodes, defineAppliedLayersTreeValues, rosreestrLayersStructure } from './mapSettingsFunctions';
import { Checkbox } from 'primereact/checkbox';
import { rosreestrLayersSettings } from '../../widgets/map/mapFeatures/mapFunctions/rosreestrFunctions/rosreestrFunctions';

const layersToShowOnPanoramaMap = ['esriWorldImagery', 'indorAvia', 'allObjects', 'okrug', 'raion', 'kvartal', 'building', 'construction', 'unfinishedBuilding', 'landPlots', 'cultureZOUIT', 'serviceZOUIT', 'natureZOUIT', 'secureZOUIT', 'otherZOUIT', 'specialZOUIT', 'forestry', 'coastlinesPolygonal', 'coastlinesLinear']
const layersToShowOnMap = ['esriWorldImagery', 'indorAvia', 'okrug', 'raion', 'kvartal', 'building', 'construction', 'unfinishedBuilding', 'landPlots', 'cultureZOUIT', 'serviceZOUIT', 'natureZOUIT', 'secureZOUIT', 'otherZOUIT', 'specialZOUIT', 'forestry', 'coastlinesPolygonal', 'coastlinesLinear']

// Страница выбора дополнительных слоев для карты
export function MapSettings() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { mapType } = useParams()
    const [additionalLayersStructure, setAdditionalLayersStructure] = useState<any>()
    const [rosreestrLayersTreeSelectedKeys, setRosreestrLayersTreeSelectedKeys] = useState<any>()
    const auxiliaryLayersDisplayState: any = useAppSelector(state => state.mapSettings.auxiliaryLayersDisplayState)
    const additionalLayersDisplayState: any = useAppSelector(state => state.mapSettings.additionalLayersDisplayState)
    const rosreestrLayersDisplayState: any = useAppSelector(state => state.mapSettings.rosreestrLayersDisplayState)
    const rosreestrIsEnabled: boolean = useAppSelector(state => state.contentHeader.rosreestrIsEnabled)
    const { mapSettingsAdditionalLayers } = getItemWebStorage('currentConfig') ?? {}
    const rosreestrLayersTreeNodes = configureLayersTreeNodes(rosreestrLayersStructure)
    const currentLayerToShowList = mapType === "map" ? layersToShowOnMap : layersToShowOnPanoramaMap

    useEffect(() => {
        if (mapSettingsAdditionalLayers && !additionalLayersStructure) {
            let newAdditionalLayersStructure = {}
            mapSettingsAdditionalLayers?.layers.map((item: any) => {
                Object.assign(newAdditionalLayersStructure, { [item.name]: item.label })
            })
            setAdditionalLayersStructure(newAdditionalLayersStructure)
        }
    }, [additionalLayersStructure])

    useEffect(() => {
        const { selectedKeys } = defineLayersTreeSelectedKeys(rosreestrLayersTreeNodes, rosreestrLayersDisplayState)
        setRosreestrLayersTreeSelectedKeys(selectedKeys)
    }, [rosreestrLayersDisplayState])

    function defineLayersTreeSelectedKeys(layersTreeNodes: any, layersDisplayState: any, selectedKeys: any = {}) {
        let previousSelectedKeys = structuredClone(selectedKeys)
        let childrenNumber = 0
        layersTreeNodes.map((treeNode: any) => {
            const { key, data, children } = treeNode
            if (children) {
                const { selectedKeys: newSelectedKeys, previousSelectedKeys, grandChildrenNumber } = defineLayersTreeSelectedKeys(treeNode?.children, layersDisplayState, selectedKeys)
                const addedSelectedKeysNumber = Object.keys(newSelectedKeys).length - Object.keys(previousSelectedKeys).length - grandChildrenNumber
                if (addedSelectedKeysNumber > 0) {
                    childrenNumber += children.length
                    if (addedSelectedKeysNumber < children.length) {
                        Object.assign(selectedKeys, { [key]: { partialChecked: true } })
                    } else {
                        Object.assign(selectedKeys, { [key]: { checked: true } })
                    }
                }
            } else if (data && layersDisplayState[data]) {
                Object.assign(selectedKeys, { [key]: { checked: true } })
            }
        })
        return { selectedKeys, previousSelectedKeys, grandChildrenNumber: childrenNumber }
    }

    function cancelAllLayers() {
        const currentMapSettingsState = { auxiliaryLayersDisplayState, additionalLayersDisplayState, rosreestrLayersDisplayState }
        const newMapSettingsState = {
            ...currentMapSettingsState,
            auxiliaryLayersDisplayState: auxiliaryLayersInitialDisplayState,
            rosreestrLayersDisplayState: rosreestrLayersInitialDisplayState,
            additionalLayersDisplayState: {}
        }
        dispatch(resetAllMapLayersState())
        setItemWebStorage('mapSettings', newMapSettingsState)
    }

    function onCheckLayerBasic(layerType: string, layerName: string, isShown: boolean) {
        // добавить общее состояние mapSettings в mapSettingsSlice
        const currentMapSettingsState: any = { auxiliaryLayersDisplayState, additionalLayersDisplayState, rosreestrLayersDisplayState }
        const currentTypeLayersState = currentMapSettingsState?.[`${layerType}LayersDisplayState`]
        const newLayersState = {
            ...currentTypeLayersState,
            [layerName]: isShown
        }
        const newMapSettingsState = {
            ...currentMapSettingsState,
            [`${layerType}LayersDisplayState`]: newLayersState
        }
        setItemWebStorage('mapSettings', newMapSettingsState)
        dispatch(setMapLayersState({ layersType: layerType, layersState: newLayersState }))
    }

    function onCheckLayerInTree(layerType: any, event: any) {
        const selectedKeys = event?.value
        // добавить обработку не только для росреестра при необходимости
        let newLayersState = defineAppliedLayersTreeValues(rosreestrLayersTreeNodes, selectedKeys)
        const currentMapSettingsState = getItemWebStorage("mapSettings")
        const currentTypeLayersState = currentMapSettingsState?.[`${layerType}LayersDisplayState`]
        newLayersState = { ...currentTypeLayersState, ...newLayersState }
        const newMapSettingsState = {
            ...currentMapSettingsState,
            [`${layerType}LayersDisplayState`]: newLayersState
        }
        setItemWebStorage('mapSettings', newMapSettingsState)
        dispatch(setMapLayersState({ layersType: layerType, layersState: newLayersState }))
    }

    function getRosreestrLayersTreeNodeTemplate(node: any) {
        const label = <span>{node.label}</span>;
        const layerName = node?.data
        const rosreestrLayerSettings: any = rosreestrLayersSettings[layerName]
        const objectsHaveLabels = rosreestrLayerSettings?.labelsName
        const labelsLayerName = `${layerName}Labels`
        const disabled = !rosreestrLayersDisplayState[layerName]
        return (
            <>
                {label}
                {layerName && objectsHaveLabels &&
                    <div className='second-checkbox-div' onClick={(event: any) => event.stopPropagation()}>
                        <Checkbox
                            className='second-checkbox'
                            inputId={labelsLayerName}
                            value={labelsLayerName}
                            onChange={(event: any) => onCheckLayerBasic('rosreestr', labelsLayerName, event.checked)}
                            checked={rosreestrLayersDisplayState[labelsLayerName]}
                            disabled={disabled}
                        />
                        <label htmlFor={labelsLayerName} className={'second-checkbox-label' + (disabled ? ' disabled' : '')}>Подписи</label>
                    </div>
                }
            </>
        )
    }

    return (
        <>
            <ContentHeader title="Настройка карты" />
            <div className="map-settings-page-div">
                <div className="layers-type-section">
                    <span>ВСПОМОГАТЕЛЬНЫЕ СЛОИ</span>
                </div>
                <div className="layers-list">
                    {/* Чекбокс отображения слоёв карты */}
                    {Object.keys(auxiliaryLayersStructure).map((layerName => {
                        if (currentLayerToShowList.includes(layerName)) {
                            return (
                                <div key={layerName} className='layers-list-item'>
                                    <Checkbox
                                        inputId={layerName}
                                        value={layerName}
                                        onChange={(event: any) => onCheckLayerBasic('auxiliary', layerName, event.checked)}
                                        checked={auxiliaryLayersDisplayState[layerName]}
                                    />
                                    <label htmlFor={layerName} className='checkbox-label'>{auxiliaryLayersStructure[layerName]}</label>
                                </div>
                            )
                        }
                    }))}
                </div>
                <hr></hr>
                {additionalLayersStructure &&
                    <>
                        <div className="layers-type-section">
                            <span>{mapSettingsAdditionalLayers?.name}</span>
                        </div>
                        <div className="layers-list">
                            {/* Чекбокс отображения слоёв карты */}
                            {Object.keys(additionalLayersStructure).map((layerName => {
                                return (
                                    <div key={layerName} className='layers-list-item'>
                                        <Checkbox
                                            inputId={layerName}
                                            value={layerName}
                                            onChange={(event: any) => onCheckLayerBasic('additional', layerName, event.checked)}
                                            checked={additionalLayersDisplayState[layerName]}
                                        />
                                        <label htmlFor={layerName} className='checkbox-label'>{additionalLayersStructure[layerName]}</label>
                                    </div>
                                )
                            }))}
                        </div>
                        <hr></hr>
                    </>
                }
                <div className="layers-type-section">
                    <span>НАСТРОЙКИ КАРТЫ РОСРЕЕСТРА
                        {!rosreestrIsEnabled &&
                            <span className='important'> (нет доступа к данным Росреестра, подключите через шапку карты)</span>
                        }
                    </span>
                </div>
                <div className='layers-tree'>
                    <Tree
                        value={rosreestrLayersTreeNodes}
                        selectionMode='checkbox'
                        selectionKeys={rosreestrLayersTreeSelectedKeys}
                        onSelectionChange={(event: any) => onCheckLayerInTree('rosreestr', event)}
                        disabled={!rosreestrIsEnabled}
                        nodeTemplate={getRosreestrLayersTreeNodeTemplate}
                    />
                </div>
                <div>
                    <Button className="apply-layers-button" onClick={() => navigate(-1)}>Применить</Button>
                    <Button className="cancel-all-layers-button" onClick={cancelAllLayers}>Сбросить</Button>
                </div>
            </div>
        </>
    );
}

